const APPROVAL_EVENT_TYPES = new Set([
  "approval_required",
  "approval_updated",
  "approval_resolved",
]);

const ATTENTION_EVENT_TYPES = new Set([
  "attention_required",
  "attention_updated",
  "attention_resolved",
]);

const TERMINAL_APPROVAL_STATUSES = new Set([
  "approved",
  "rejected",
  "cancelled",
  "expired",
]);

const text = (value) => typeof value === "string" && value.trim()
  ? value.trim()
  : null;

const textList = (value) => (Array.isArray(value) ? value : [value])
  .map(text)
  .filter(Boolean);

const recordId = (event, kind) => text(
  event?.[kind]?.id || event?.[`${kind}Id`] || event?.id,
);

const eventPayload = (event, kind) => {
  const nested = event?.[kind];
  return nested && typeof nested === "object" ? nested : event;
};

export function createInitialApprovalAttentionState(conversationId = null) {
  return {
    conversationId,
    approvals: {},
    attention: {},
    seenEventIds: [],
  };
}

function eventWasSeen(state, event) {
  const eventId = text(event?.eventId);
  return eventId ? state.seenEventIds.includes(eventId) : false;
}

function rememberEvent(state, event) {
  const eventId = text(event?.eventId);
  if (!eventId) return state.seenEventIds;
  return [...state.seenEventIds, eventId].slice(-100);
}

function reduceApproval(state, event) {
  const id = recordId(event, "approval");
  if (!id) return state;
  const payload = eventPayload(event, "approval");
  const previous = state.approvals[id];

  if (previous && TERMINAL_APPROVAL_STATUSES.has(previous.status)) return state;

  if (event.type === "approval_resolved") {
    if (!previous) return state;
    const status = text(payload.status || event.status);
    if (!TERMINAL_APPROVAL_STATUSES.has(status)) return state;
    return {
      ...state,
      approvals: {
        ...state.approvals,
        [id]: {
          ...previous,
          id,
          status,
          resolution: text(payload.resolution || event.resolution),
          resolvedAt: text(payload.resolvedAt || event.resolvedAt),
        },
      },
      seenEventIds: rememberEvent(state, event),
    };
  }

  const title = text(payload.title);
  const summary = text(payload.summary || payload.message || event.content);
  if (!previous && !title && !summary) return state;

  return {
    ...state,
    approvals: {
      ...state.approvals,
      [id]: {
        ...previous,
        id,
        status: "pending",
        title: title || previous?.title || "Approval required",
        summary: summary || previous?.summary,
        consequence: text(payload.consequence) || previous?.consequence || null,
        scope: payload.scope === undefined
          ? previous?.scope || []
          : textList(payload.scope),
        requestedAt: text(payload.requestedAt) || previous?.requestedAt || null,
        error: text(payload.error) || previous?.error || null,
      },
    },
    seenEventIds: rememberEvent(state, event),
  };
}

function reduceAttention(state, event) {
  const id = recordId(event, "attention");
  if (!id) return state;
  const payload = eventPayload(event, "attention");
  const previous = state.attention[id];

  if (previous?.status === "resolved") return state;
  if (event.type === "attention_resolved") {
    if (!previous) return state;
    return {
      ...state,
      attention: {
        ...state.attention,
        [id]: { ...previous, status: "resolved" },
      },
      seenEventIds: rememberEvent(state, event),
    };
  }

  const title = text(payload.title);
  const summary = text(payload.summary || payload.message || event.content);
  if (!previous && !title && !summary) return state;

  return {
    ...state,
    attention: {
      ...state.attention,
      [id]: {
        ...previous,
        id,
        status: "open",
        title: title || previous?.title || "Needs your attention",
        summary: summary || previous?.summary,
        message: summary || previous?.message,
        consequence: text(payload.consequence) || previous?.consequence || null,
        severity: ["info", "warning", "danger", "success"].includes(payload.severity)
          ? payload.severity
          : previous?.severity || "info",
      },
    },
    seenEventIds: rememberEvent(state, event),
  };
}

export function approvalAttentionReducer(state, action) {
  if (action.type === "workflow/reset") {
    return createInitialApprovalAttentionState(action.conversationId || null);
  }
  if (action.type !== "workflow/event" || !action.event || eventWasSeen(state, action.event)) {
    return state;
  }
  if (APPROVAL_EVENT_TYPES.has(action.event.type)) return reduceApproval(state, action.event);
  if (ATTENTION_EVENT_TYPES.has(action.event.type)) return reduceAttention(state, action.event);
  return state;
}

export function selectApprovalAttentionPresentation(state) {
  return {
    approvals: Object.values(state.approvals),
    attentionItems: Object.values(state.attention).filter((item) => item.status === "open"),
  };
}
